import React, { Dispatch, SetStateAction, useEffect, useState, useCallback } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';
import TabNavigation from 'components/base/tab-navigation';
import ItemDetailsTab from './item-details-tab';
import VendorDetailsTab from './vendor-details-tab';
import { STORE_ITEM_PREVIEW_TABS } from './utils';

const InventoryStorePreview = ({
  setReviewedStoreItems
}: {
  reviewedStoreItems: number[];
  setReviewedStoreItems: Dispatch<SetStateAction<number[]>>;
}) => {
  const { storeItemArray, setStoreItemArray } = useInventoryStoreContext();
  const { setHideSideBar, setIsBackLink, setBackLinkTitle } = useDashboardContext();

  const [activeTab, setActiveTab] = useState<string>(STORE_ITEM_PREVIEW_TABS.ITEM_DETAILS);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);

  const tabs = [
    { label: 'Item Details', value: STORE_ITEM_PREVIEW_TABS.ITEM_DETAILS },
    { label: 'Vendor Details', value: STORE_ITEM_PREVIEW_TABS.VENDOR_DETAILS }
  ];

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to inventory');

    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
      setHideSideBar(false);
    };
  }, [setIsBackLink, setBackLinkTitle, setHideSideBar]);

  const handleItemInputChange = useCallback(
    (index: number, field: string, value: string) => {
      const updatedItems = storeItemArray.map((item, i) => {
        if (i === index) {
          if (field === 'shelfLife') {
            return { ...item, [field]: Number(value) || 0 };
          }
          return { ...item, [field]: value };
        }
        return item;
      });
      setStoreItemArray(updatedItems);
    },
    [storeItemArray, setStoreItemArray]
  );

  const handleEditItem = useCallback((index: number) => {
    setEditingItemIndex(index);
  }, []);

  const handleSaveItem = useCallback(() => {
    setEditingItemIndex(null);
  }, []);

  const handleDeleteItem = useCallback(
    (index: number) => {
      setStoreItemArray(storeItemArray.filter((_, i) => i !== index));
      setReviewedStoreItems((prev) => prev.filter((itemIndex) => itemIndex !== index));
    },
    [storeItemArray, setStoreItemArray, setReviewedStoreItems]
  );

  const handleVendorChange = useCallback(
    (itemIndex: number, vendorIndex: number, selectedVendor: any) => {
      const updatedItems = storeItemArray.map((item, i) => {
        if (i === itemIndex) {
          const updatedVendors = item.vendors.map((vendor, vIdx) =>
            vIdx === vendorIndex
              ? { ...vendor, id: selectedVendor.id, name: selectedVendor.name }
              : vendor
          );
          return { ...item, vendors: updatedVendors };
        }
        return item;
      });
      setStoreItemArray(updatedItems);
    },
    [storeItemArray, setStoreItemArray]
  );

  const handleVendorPriceChange = useCallback(
    (itemIndex: number, vendorIndex: number, price: string) => {
      const updatedItems = storeItemArray.map((item, i) => {
        if (i === itemIndex) {
          const updatedVendors = item.vendors.map((vendor, vIdx) =>
            vIdx === vendorIndex ? { ...vendor, price } : vendor
          );
          return { ...item, vendors: updatedVendors };
        }
        return item;
      });
      setStoreItemArray(updatedItems);
    },
    [storeItemArray, setStoreItemArray]
  );

  const handleAddVendor = useCallback(
    (itemIndex: number) => {
      const updatedItems = storeItemArray.map((item, i) => {
        if (i === itemIndex) {
          return {
            ...item,
            vendors: [...item.vendors, { id: '', name: '', price: '' }]
          };
        }
        return item;
      });
      setStoreItemArray(updatedItems);
    },
    [storeItemArray, setStoreItemArray]
  );

  const handleDeleteVendor = useCallback(
    (itemIndex: number, vendorIndex: number) => {
      const updatedItems = storeItemArray.map((item, i) => {
        if (i === itemIndex) {
          const updatedVendors = item.vendors.filter((_, vIdx) => vIdx !== vendorIndex);
          return { ...item, vendors: updatedVendors };
        }
        return item;
      });
      setStoreItemArray(updatedItems);
    },
    [storeItemArray, setStoreItemArray]
  );

  const removeItemWithNoVendors = useCallback(() => {
    const indexMap = new Map<number, number>();
    const filteredItems = storeItemArray.reduce<typeof storeItemArray>((acc, item, index) => {
      if (item.vendors?.length) {
        indexMap.set(index, acc.length);
        acc.push(item);
      }
      return acc;
    }, []);

    setStoreItemArray(filteredItems);
    setReviewedStoreItems((prev) =>
      prev
        .map((oldIndex) => indexMap.get(oldIndex))
        .filter((nextIndex): nextIndex is number => nextIndex !== undefined)
    );
  }, [storeItemArray, setStoreItemArray, setReviewedStoreItems]);

  return (
    <div className='w-full mx-auto'>
      <div className='flex justify-between'>
        <div className='flex justify-between mb-6'>
          <h3 className='text-xl font-semibold'>Review Store Items</h3>
        </div>
        {storeItemArray.some((item) => !item.vendors?.length) && (
          <p className='cursor-pointer text-red-5' onClick={removeItemWithNoVendors}>
            - Remove all Item without vendors
          </p>
        )}
      </div>
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === STORE_ITEM_PREVIEW_TABS.ITEM_DETAILS && (
        <ItemDetailsTab
          editingItemIndex={editingItemIndex}
          onItemInputChange={handleItemInputChange}
          onEditItem={handleEditItem}
          onSaveItem={handleSaveItem}
          onDeleteItem={handleDeleteItem}
        />
      )}

      {activeTab === STORE_ITEM_PREVIEW_TABS.VENDOR_DETAILS && (
        <VendorDetailsTab
          onVendorChange={handleVendorChange}
          onVendorPriceChange={handleVendorPriceChange}
          onAddVendor={handleAddVendor}
          onDeleteVendor={handleDeleteVendor}
        />
      )}
    </div>
  );
};

export default InventoryStorePreview;
